import React, { useCallback, useMemo, useRef } from 'react';
import { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import Modal from 'components/Modal';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import tierService from 'utils/network/tier/tier.service';
import { useNotif } from 'context/NotificationContext';
import { STATUS_NULL, PAGINATION, STATUS_SUCCESS } from 'utils/constants';

export const tierDummies = [
  { id: 1, name: 'Crew' },
  { id: 2, name: 'Co-Pilot' },
  { id: 3, name: 'Pilot' },
  { id: 4, name: 'Spacetronot' }
];

export default function MasterTier() {
  const columns = [
    {
      name: 'Name',
      sortable: true,
      selector: 'tier_name',
      cell: function render(row) {
        return <>{row.tier_name}</>;
      }
    },
    {
      name: 'Actions',
      cell: function render(row) {
        return (
          <div className="w-max">
            <button
              className="p-1 text-center bg-gray-100 border rounded-lg leading-5 transition-colors duration-150 active:bg-purple-600 hover:bg-gray-300 focus:outline-none focus:shadow-outline-gray"
              onClick={handleCreateEdit(row)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
            <button
              className="p-1 ml-2 text-center bg-gray-100 border rounded-lg leading-5 transition-colors duration-150 active:bg-purple-600 hover:bg-gray-300 focus:outline-none focus:shadow-outline-gray"
              onClick={handleDelete(row)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        );
      }
    }
  ];

  const { setNotification, notification } = useNotif();
  const [pagination, setPagination] = useState({
    search: PAGINATION.DEFAULT_KEYWORD,
    page: PAGINATION.DEFAULT_PAGE,
    perPage: PAGINATION.DEFAULT_PER_PAGE,
    totalPage: PAGINATION.DEFAULT_TOTAL_PAGE,
    totalData: PAGINATION.DEFAULT_TOTAL_DATA
  });
  const [form, setForm] = useState({
    id: null,
    name: ''
  });
  const [createEdit, setCreateEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [sortProp, setSortProp] = useState({
    order: 'created_at',
    sort: 'desc'
  });

  const query = useQueryClient();
  const queryKey = useMemo(() => {
    return `tier-list--${JSON.stringify({
      page: pagination.page,
      perPage: pagination.perPage,
      search: pagination.search,
      sortProp
    })}`;
  }, [pagination.page, pagination.perPage, pagination.search, sortProp]);

  // get list
  const { data, isLoading: getTierLoading } = useQuery({
    queryKey,
    queryFn: () => {
      return tierService.list({
        limit: pagination.perPage,
        tier_name: pagination.search,
        offset: pagination.page,
        order: sortProp.order,
        sort: sortProp.sort
      });
    },
    onSuccess: (data) => {
      if (data?.data === STATUS_NULL) {
        setNotification({
          ...notification,
          show: true,
          title: 'Error',
          error: true,
          message: data?.error_message
        });
      }
    }
  });

  // create or update
  const { mutate: createUpdateTier } = useMutation(
    (body) => {
      if (body.id) {
        return tierService.put(body);
      }
      return tierService.post(body);
    },
    {
      onSuccess: (data) => {
        if (data?.status === STATUS_SUCCESS) {
          setNotification({
            ...notification,
            show: true,
            success: true,
            title: 'Success',
            message: `Tier successfully ${form?.id ? 'updated' : 'created'}`
          });
          query.invalidateQueries([queryKey]);
          closeCreateEdit();
        }
      },
      onError: () => {
        setNotification({
          ...notification,
          show: true,
          title: 'Error',
          error: true,
          message: 'Error create tier'
        });
      }
    }
  );

  // delete
  const { mutate: deleteTier } = useMutation(
    (id) => {
      return tierService.delete(id);
    },
    {
      onSuccess: (data) => {
        if (data?.status === STATUS_SUCCESS) {
          query.invalidateQueries([queryKey]);
          closeDelete();
          setNotification({
            ...notification,
            success: true,
            show: true,
            title: 'Success',
            message: 'Tier successfully deleted'
          });
        }
      },
      onError: (error) => {
        setNotification({
          ...notification,
          show: true,
          title: 'Error',
          error: true,
          message: error?.response?.data?.message
        });
      }
    }
  );

  function handlePerRowsChange(newPerPage, page) {
    setPagination({
      ...pagination,
      page,
      perPage: newPerPage,
      totalPage: Math.ceil(data?.data?.length / newPerPage)
    });
  }

  function handlePageChange(page) {
    setPagination({
      ...pagination,
      page
    });
  }

  const handleCreateEdit = (row) => () => {
    if (row)
      setForm({
        ...form,
        name: row.tier_name,
        id: row.id
      });
    setCreateEdit(true);
  };

  function closeCreateEdit() {
    setCreateEdit(false);
  }

  const handleDelete = (row) => () => {
    setForm({ ...form, id: row.id });
    setShowDelete(true);
  };

  function closeDelete() {
    setShowDelete(false);
  }

  function handleChangeForm(e) {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  }

  const handleSort = useCallback((column, sortDirection) => {
    setSortProp({
      order: column.selector,
      sort: sortDirection
    });
  });

  useEffect(() => {
    if (!createEdit && !showDelete)
      setForm({
        ...form,
        name: '',
        id: null
      });
  }, [createEdit, showDelete]);

  function handleCreateEditMasterTier() {
    createUpdateTier({ ...form, tier_name: form.name });
  }

  function handleDeleteMasterTier() {
    deleteTier(form.id);
  }

  const searchDebounce = useRef();

  return (
    <div>
      <div className="flex flex-row items-center justify-between">
        <h2 className="my-6 text-xl font-semibold text-gray-700 dark:text-gray-200">
          Master Tier
        </h2>
        <button
          className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
          onClick={handleCreateEdit(null)}
        >
          Add New
        </button>
      </div>

      <div className="pb-3 grid grid-cols-1 md:grid-cols-4">
        <div className="flex flex-row items-center gap-3">
          <div className="relative">
            <input
              className="block w-full px-3 py-2 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
              placeholder="John Doe"
              name="search"
              // value={pagination.search}
              onChange={({ target: { value } }) => {
                if (searchDebounce.current) {
                  clearTimeout(searchDebounce.current);
                }

                searchDebounce.current = setTimeout(() => {
                  setPagination({
                    ...pagination,
                    search: value
                  });
                }, 800);
              }}
            />
            <div className="absolute top-0 bottom-0 flex flex-row items-center h-full right-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>
        <div></div>
        <div></div>
        <div></div>
      </div>
      <DataTable
        columns={columns}
        data={data?.data || []}
        responsive
        sortServer
        onSort={handleSort}
        progressPending={getTierLoading}
        pagination
        paginationServer
        paginationTotalRows={data?.total_data || 0}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
      />

      {/* Create Edit Section */}
      <Modal
        show={createEdit}
        onClose={closeCreateEdit}
        title={form.id ? 'Edit Tier' : 'Create Tier'}
        footer={
          <div className="flex flex-row-reverse">
            <button
              className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
              onClick={handleCreateEditMasterTier}
            >
              {form.id ? 'Edit' : 'Create'}
            </button>
          </div>
        }
      >
        <label className="block mt-3 text-sm">
          <span className="text-gray-700 dark:text-gray-400">
            Name <span className="text-red-500">*</span>
          </span>
          <input
            className="block w-full px-3 py-2 mt-1 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
            placeholder="Administrator"
            name="name"
            value={form.name}
            onChange={handleChangeForm}
          />
        </label>
      </Modal>

      {/* Delete Section */}
      <Modal
        show={showDelete}
        onClose={closeDelete}
        title="Delete Confirmation"
        footer={
          <div className="flex flex-row-reverse">
            <button
              className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
              onClick={closeDelete}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 mt-3 text-sm font-medium text-center border border-transparent rounded-lg leading-5 transition-colors duration-150 focus:outline-none focus:shadow-outline-purple"
              onClick={handleDeleteMasterTier}
            >
              Yes, Delete
            </button>
          </div>
        }
      >
        <p>Are you sure to delete this item?</p>
      </Modal>
    </div>
  );
}
